import type { CreateRoundSnapshotInput } from "../round/round-types.js";
import { RoundCommandError } from "../round/round-errors.js";
import {
  measuredActivityIds,
  isCountChallenge,
  isClosestChallenge,
  challengeCountUnit,
} from "./measured-activities.js";
import type {
  MeasuredAttempt,
  MeasuredChallengeRules,
} from "./measured-types.js";

export function validateMeasuredChallenge(
  input: CreateRoundSnapshotInput,
): void {
  const rule = input.rules.measuredChallenge;
  if (!rule) return;
  if (
    rule.version !== 1 ||
    input.schemaVersion !== undefined ||
    input.rules.kind !== "golf-challenge" ||
    input.rules.format !== "high-low" ||
    input.rules.handicapMode !== "off" ||
    input.course.holes.length !== 0 ||
    input.course.rating !== null ||
    input.course.slope !== null ||
    input.participants.some((p) => p.teamId !== null)
  )
    fail(
      "Measured challenges have attempts, not golf holes, handicaps or teams.",
    );
  if (
    !measuredActivityIds.includes(rule.activity) ||
    !["m", "yd", "made-putts", "successes"].includes(rule.unit) ||
    !["shared", "best-next"].includes(rule.tieRule) ||
    !["outdoor", "simulator", "unknown"].includes(rule.environment) ||
    !Number.isInteger(rule.attemptCount) ||
    rule.attemptCount < 1 ||
    rule.attemptCount > 50
  )
    fail("Choose a supported activity and one to fifty attempts.");
  const counted = isCountChallenge(rule.activity);
  if (
    (counted
      ? rule.unit !== challengeCountUnit(rule.activity)
      : !["m", "yd"].includes(rule.unit)) ||
    (counted && rule.tieRule !== "shared") ||
    input.rules.metric?.unit !== rule.unit ||
    input.rules.metric.lowerWins !== isClosestChallenge(rule.activity)
  )
    fail("The activity, unit and tie rule must agree.");
  if (
    rule.summary &&
    (!uuid(rule.summary.id) ||
      !rule.summary.ownerAccountId.trim() ||
      !rule.summary.name.trim() ||
      rule.summary.name.length > 80)
  )
    fail("Choose a valid challenge summary before starting.");
  if (
    rule.includeInMainSummary !== undefined &&
    typeof rule.includeInMainSummary !== "boolean"
  )
    fail("Choose whether this challenge counts in the main summary.");
  if (
    rule.includeInMainSummary &&
    (rule.summary || input.participants.length !== 2)
  )
    fail(
      "Main summary requires two players and cannot also use a separate summary.",
    );
}

export function validateMeasuredAttempt(
  rule: MeasuredChallengeRules,
  entry: MeasuredAttempt,
): void {
  if (!["unset", "miss", "value"].includes(entry.status))
    fail("Choose a recorded attempt or a miss.");
  if (entry.status !== "value") {
    if (entry.value !== null)
      fail("An unknown or missed attempt has no measured distance.");
    return;
  }
  if (
    entry.value === null ||
    !Number.isFinite(entry.value) ||
    entry.value < 0 ||
    entry.value > 2000 ||
    (isCountChallenge(rule.activity) && entry.value !== 1)
  )
    fail(
      rule.activity === "putting"
        ? "Record each putt as made or missed."
        : isCountChallenge(rule.activity)
          ? "Record each attempt as a success or a miss."
          : "Enter a distance from zero to 2000 in the agreed unit.",
    );
}

function uuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu.test(
    value,
  );
}
function fail(message: string): never {
  throw new RoundCommandError(message);
}
